import { omit, pick } from "lodash";
import * as React from "react";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { User } from "../../normalizr/entities";
import { getAvailableTeams, loadTeamsRequest } from "./teams";
import { loadAvailableUsersRequest } from "./users/actions";
import { createAvailableUsersSelector, getAllUsers, simpleIdsGetter } from "./users/selectors";

export const useAvailableTeams = () => {
    const dispatch = useDispatch();
    React.useEffect(() => {
        dispatch(loadTeamsRequest());
    }, []);

    const teams = useSelector(getAvailableTeams);

    return [teams, !!teams] as const;
};

const emptyArray: number[] = [];
export type AvailableUsersParams = {
    selectedUsers?: number[];
    missedUsersLoader?: F1<number[], PromiseLike<User[]>>;
    visibleUsers?: number[];
    /**
     * Add prop `excludedUsersIds` - the opposite of `visibleUsers`; an array of user's ids to be excluded from the general list.
     * For example: adding a member of a space, you need to exclude users who are already members of that space
     */
    excludedUsersIds?: number[];
};

export const useAvailableUsers = (data: AvailableUsersParams) => {
    const dispatch = useDispatch();
    const selectedUsers = data.selectedUsers || emptyArray;

    React.useEffect(() => {
        dispatch(
            loadAvailableUsersRequest({
                requiredUserIds: selectedUsers,
                missedUsersLoader: data.missedUsersLoader,
            })
        );
    }, [selectedUsers]);

    const selector = useMemo(() => createAvailableUsersSelector(simpleIdsGetter), []);
    const users = useSelector(state => selector(state, selectedUsers));

    const isLoaded = useMemo(() => {
        return !!users && selectedUsers.every(id => users[id]);
    }, [users, selectedUsers]);

    const visibleUsers = useMemo(() => {
        let result = users;

        if (data.visibleUsers) {
            result = pick(users, data.visibleUsers);
        }

        if (data.excludedUsersIds) {
            result = omit(users, data.excludedUsersIds);
        }

        return result;
    }, [users, data.visibleUsers, data.excludedUsersIds]);

    return [visibleUsers, isLoaded] as const;
};

export const useUser = (userId: number) => {
    const users = useSelector(getAllUsers);
    return users?.[userId] || null;
};
